<script setup lang="ts">
import type { PropType } from 'vue';

import type { SaleItemEntity } from '~/types/sale.type';

// PROPS
const { sale } = defineProps({
    sale: { type: Object as PropType<SaleItemEntity>, required: true },
});

// COMPOSABLES
const { tt } = useTypedI18n();
const { trackGalleryView } = useTracking();

// DATA
const baseImagesCarousel = useTemplateRef('baseImagesCarousel');
const imageLightboxOpen = ref<boolean>(false);
const videoLightboxOpen = ref<boolean>(false);
const baseImagesSlideIndex = ref<number>(0);

const baseImages = computed(() => sale.mainAsset.images.base);

const videos = computed<string[]>(() => {
    const { engine, gearbox, other } = sale.mainAsset.videos;

    return [...engine, ...gearbox, ...other];
});

// HANDLERS
const onBaseImagesCarouselSelect = (index: number): void => {
    baseImagesSlideIndex.value = index;
};

const openImageLightbox = (): void => {
    imageLightboxOpen.value = true;
    trackGalleryView('photo', 'main');
};

const onLightboxIndexChange = (index: number): void => {
    baseImagesCarousel.value?.emblaApi?.scrollTo(index);
};

const openVideoLightbox = (): void => {
    videoLightboxOpen.value = true;
    trackGalleryView('video', 'main');
};

const { saleImageAlt } = useA11y();
</script>

<template>
    <div class="relative">
        <UCarousel
            v-if="baseImages.length"
            ref="baseImagesCarousel"
            v-slot="{ item, index }"
            :items="baseImages"
            arrows
            :ui="{ item: 'basis-[85%]', prev: 'hidden md:flex md:start-4', next: 'hidden md:flex md:end-4' }"
            @select="onBaseImagesCarouselSelect"
        >
            <div class="relative">
                <NuxtImg
                    class="aspect-4/3 cursor-grab rounded-xl border active:cursor-grabbing"
                    :src="item"
                    :width="800"
                    densities="x1"
                    loading="lazy"
                    :draggable="false"
                    :alt="saleImageAlt(sale.mainAsset, item)"
                />
                <UBadge
                    color="primary-dark"
                    class="absolute top-2 left-2 z-10 md:top-4 md:left-4"
                    :class="{ hidden: index !== baseImagesSlideIndex }"
                    :label="`${index + 1}/${baseImages.length + 1}`"
                />
                <SaleTrustReportInspectionBadge class="absolute bottom-5 left-2 z-10 md:bottom-4 md:left-4" :sale="sale" />
                <SdpLightboxButton
                    class="absolute top-2 right-2 z-10 md:top-4 md:right-4"
                    :class="{ hidden: index !== baseImagesSlideIndex }"
                    @click="openImageLightbox"
                />
            </div>
        </UCarousel>

        <UButton
            v-if="videos.length"
            color="neutral"
            variant="outline"
            class="absolute -bottom-4 left-1/2 -translate-x-1/2"
            :label="tt('sdp.videoGallery', { totalVideos: videos.length })"
            icon="lucide:video"
            leading
            @click="openVideoLightbox"
        />
    </div>

    <LazySdpLightboxImage
        v-model="imageLightboxOpen"
        :items="baseImages"
        :initial-slide="baseImagesSlideIndex"
        @active-index-change="onLightboxIndexChange"
    />

    <LazySdpLightboxVideo v-model="videoLightboxOpen" :main-asset="sale.mainAsset" />
</template>
